import { useEffect, useState } from 'react';
import type { AnalysisSpec, SemanticFilter } from '@lognai/plugin-analytics';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lognai/synapse-design-system-v2';
import { Plus, Trash2, X } from '@lognai/synapse-design-system-v2/icons';
import { executeAnalysis } from '../api';
import { memberLabel, validDateRange, type CreatorCatalog } from '../creator-helpers';
import { businessLabel } from '../../lib/user-facing';

const operators: Array<{ value: SemanticFilter['operator']; label: string }> = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Does not equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'gt', label: 'Greater than' },
  { value: 'gte', label: 'At least' },
  { value: 'lt', label: 'Less than' },
  { value: 'lte', label: 'At most' },
  { value: 'inDateRange', label: 'Date range' },
  { value: 'set', label: 'Has a value' },
  { value: 'notSet', label: 'Has no value' },
];

export function FilterBuilder({
  catalog,
  analysis,
  onChange,
  disabled,
}: {
  catalog?: CreatorCatalog;
  analysis: AnalysisSpec;
  onChange: (filters: SemanticFilter[]) => void;
  disabled?: boolean;
}) {
  const [member, setMember] = useState('');
  const [operator, setOperator] = useState<SemanticFilter['operator']>('equals');
  const [value, setValue] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [range, setRange] = useState<[string, string]>(['', '']);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const members = [
    ...new Set([
      ...(catalog?.dimensions ?? []),
      ...(catalog?.timeDimensions ?? []),
      ...Object.keys(catalog?.measures ?? {}),
    ]),
  ];
  const isDate = catalog?.timeDimensions.includes(member);
  const isCategory = catalog?.dimensions.includes(member) && !isDate;
  const multiple = isCategory && ['equals', 'notEquals'].includes(operator);
  const noValue = ['set', 'notSet'].includes(operator);
  const filterKey = JSON.stringify(analysis.filters.filter((filter) => filter.member !== member));
  const dateKey = JSON.stringify(analysis.timeDimension?.dateRange);
  useEffect(() => {
    setMember('');
    setSelected([]);
    setValue('');
  }, [analysis.view]);
  useEffect(() => {
    setSelected([]);
    setValue('');
    setRange(['', '']);
  }, [member, operator]);
  useEffect(() => {
    let live = true;
    setSuggestions([]);
    setUnavailable(false);
    setLoading(false);
    if (!multiple || !analysis.measures.length || disabled) return;
    setLoading(true);
    const filters: SemanticFilter[] = JSON.parse(filterKey);
    if (analysis.timeDimension?.dateRange)
      filters.push({
        member: analysis.timeDimension.member,
        operator: 'inDateRange',
        values: analysis.timeDimension.dateRange,
      });
    void executeAnalysis({
      title: 'Available filter values',
      view: analysis.view,
      measures: analysis.measures.slice(0, 1),
      dimensions: [member],
      filters,
      order: [{ member, direction: 'asc' }],
      template: 'horizontal_bar',
      limit: 100,
      drillPath: [],
    })
      .then((result) => {
        if (live)
          setSuggestions([
            ...new Set(
              (result.rows ?? [])
                .map((row) => row[member])
                .filter((item) => item !== null && item !== undefined)
                .map(String)
            ),
          ]);
      })
      .catch(() => {
        if (live) setUnavailable(true);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [analysis.view, member, multiple, filterKey, dateKey, disabled, analysis.measures.join(',')]);
  const values = noValue
    ? undefined
    : operator === 'inDateRange'
      ? range
      : multiple
        ? [...new Set([...selected, ...(value.trim() ? [value.trim()] : [])])]
        : value.trim()
          ? [value.trim()]
          : [];
  const valid =
    member &&
    members.includes(member) &&
    (noValue || operator === 'inDateRange' ? noValue || validDateRange(range) : !!values?.length) &&
    (values?.length ?? 0) <= 50;
  return (
    <div className="space-y-3">
      <Select
        value={member || undefined}
        onValueChange={(next) => {
          setMember(next);
          setOperator('equals');
        }}
        disabled={disabled}
      >
        <SelectTrigger aria-label="Filter field">
          <SelectValue placeholder="Choose a field" />
        </SelectTrigger>
        <SelectContent>
          {members.map((item) => (
            <SelectItem key={item} value={item}>
              {memberLabel(item, catalog)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={operator}
        onValueChange={(next) => setOperator(next as SemanticFilter['operator'])}
        disabled={disabled}
      >
        <SelectTrigger aria-label="Filter operator">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operators
            .filter((item) => item.value !== 'inDateRange' || isDate)
            .map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
      {!noValue &&
        (operator === 'inDateRange' ? (
          <div className="grid grid-cols-2 gap-2">
            <Input
              aria-label="Filter start date"
              type="date"
              value={range[0]}
              onChange={(event) => setRange([event.target.value, range[1]])}
              disabled={disabled}
            />
            <Input
              aria-label="Filter end date"
              type="date"
              value={range[1]}
              onChange={(event) => setRange([range[0], event.target.value])}
              disabled={disabled}
            />
          </div>
        ) : (
          <>
            {multiple && (
              <>
                <Select
                  value=""
                  onValueChange={(index) =>
                    setSelected((current) =>
                      [...new Set([...current, suggestions[Number(index)]])].slice(0, 50)
                    )
                  }
                  disabled={disabled || loading || !suggestions.length}
                >
                  <SelectTrigger aria-label="Choose filter value">
                    <SelectValue placeholder={loading ? 'Loading values…' : 'Choose a value'} />
                  </SelectTrigger>
                  <SelectContent>
                    {suggestions.map((item, index) => (
                      <SelectItem
                        key={item}
                        value={String(index)}
                        disabled={selected.includes(item)}
                      >
                        {item === '' ? '(Empty text)' : item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  {unavailable
                    ? 'Suggested values are unavailable. Enter an exact value below.'
                    : 'Suggestions include up to 100 values. You can also enter an exact value.'}
                </p>
              </>
            )}
            <div className="flex gap-2">
              <Input
                aria-label="Filter value"
                value={value}
                onChange={(event) => setValue(event.target.value)}
                placeholder={multiple ? 'Or enter an exact value' : 'Value'}
                disabled={disabled}
              />
              {multiple && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={disabled || !value.trim() || selected.length >= 50}
                  onClick={() => {
                    setSelected((current) => [...new Set([...current, value.trim()])]);
                    setValue('');
                  }}
                >
                  Use value
                </Button>
              )}
            </div>
            {multiple && selected.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {selected.map((item) => (
                  <button
                    type="button"
                    key={item}
                    className="flex max-w-full items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs"
                    disabled={disabled}
                    aria-label={`Remove value ${item}`}
                    onClick={() =>
                      setSelected((current) => current.filter((entry) => entry !== item))
                    }
                  >
                    <span className="truncate">{item || '(Empty text)'}</span>
                    <X size={12} />
                  </button>
                ))}
              </div>
            )}
          </>
        ))}
      <Button
        size="sm"
        variant="secondary"
        leftIcon={<Plus size={14} />}
        disabled={disabled || !valid || analysis.filters.length >= 10}
        onClick={() => {
          onChange([...analysis.filters, { member, operator, ...(values ? { values } : {}) }]);
          setValue('');
          setSelected([]);
        }}
      >
        Add filter
      </Button>
      {analysis.filters.map((filter, index) => (
        <div
          key={`${filter.member}-${index}`}
          className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-xs"
        >
          <span className="min-w-0 flex-1 break-words">
            {memberLabel(filter.member, catalog)} {businessLabel(filter.operator)}{' '}
            {filter.values?.join(', ')}
          </span>
          <button
            type="button"
            aria-label={`Remove filter ${index + 1}`}
            disabled={disabled}
            className="rounded p-1 focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => onChange(analysis.filters.filter((_, i) => i !== index))}
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
